import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import SortTabs from "@/components/base/SortTabs";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import SoftwareCard from "@/components/base/SoftwareCard";
import Toast from "@/components/base/Toast";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  filterSoftware,
  getCategoryById,
  paginate,
  totalPages,
} from "@/utils/software";
import type { Software, SortKey } from "@/utils/software";
import { toneIconWrap } from "@/utils/ui";

const PAGE_SIZE = 6;

export default function Category() {
  const { categoryId = "" } = useParams();
  const navigate = useNavigate();
  const category = getCategoryById(categoryId);

  const [sub, setSub] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState<string | null>(null);

  useDocumentMeta({
    title: category
      ? `${category.name} - 黄金网站软件app免费`
      : "软件分类 - 黄金网站软件app免费",
    description: category
      ? `黄金网站软件app免费为你整理${category.name}推荐与免费下载，包含实时行情、价格监控等实用工具，帮助投资者高效掌握黄金市场。`
      : "黄金投资软件分类导航，按趋势分析、价格监控、理财工具与行情资讯分类查找。",
    keywords: category
      ? `${category.name},黄金投资软件,黄金软件免费下载,${category.subCategories.join(",")}`
      : "黄金投资软件分类,黄金软件下载",
  });

  const results = category
    ? filterSoftware({
        categoryId,
        subCategory: sub === "全部" ? undefined : sub,
        sort,
      })
    : [];
  const pageCount = totalPages(results.length, PAGE_SIZE);
  const paged = paginate(results, page, PAGE_SIZE);

  const changeSub = (value: string) => {
    setSub(value);
    setPage(1);
  };
  const changeSort = (value: SortKey) => {
    setSort(value);
    setPage(1);
  };
  const resetFilters = () => {
    setSub("全部");
    setSort("latest");
    setPage(1);
  };
  const handleDownload = (item: Software) => {
    setToast(`已打开「${item.name}」的示范下载链接（演示功能，无真实下载）`);
  };

  return (
    <SiteLayout>
      <div className="container-page py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "软件分类", to: "/softwares" },
            { label: category?.name ?? "未知分类" },
          ]}
        />

        {!category ? (
          <div className="mt-8">
            <EmptyState
              icon="ri-error-warning-line"
              title="分类不存在"
              desc="你访问的软件分类暂时无法找到，返回全部软件继续浏览吧。"
              actionLabel="查看全部软件"
              onAction={() => navigate("/softwares")}
            />
          </div>
        ) : (
          <>
            <section className="mt-6 overflow-hidden rounded-lg bg-secondary-950 p-6 md:p-8">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-4">
                  <span
                    className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl text-3xl ${
                      toneIconWrap[category.tone] ?? toneIconWrap.primary
                    }`}
                  >
                    <i className={category.icon} />
                  </span>
                  <div>
                    <h1 className="font-heading text-2xl font-bold text-background-50">
                      {category.name}
                    </h1>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-background-300">
                      {category.desc}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-start rounded-lg border border-secondary-700 px-5 py-3 md:items-center">
                  <span className="font-heading text-2xl font-bold text-primary-300">
                    {results.length}
                  </span>
                  <span className="text-xs text-background-400">款软件可下载</span>
                </div>
              </div>
            </section>

            <section className="mt-6 flex flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  {["全部", ...category.subCategories].map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => changeSub(item)}
                      className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        sub === item
                          ? "border-primary-500 bg-primary-500 text-foreground-950"
                          : "border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-700"
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <SortTabs value={sort} onChange={changeSort} />
              </div>

              {paged.length > 0 ? (
                <div
                  className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
                  data-product-shop
                >
                  {paged.map((item) => (
                    <SoftwareCard key={item.id} item={item} onDownload={handleDownload} />
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="ri-search-eye-line"
                  title="暂无相关软件"
                  desc={`「${sub}」条件下暂时没有符合条件的软件，试试重置筛选。`}
                  actionLabel="重置筛选"
                  onAction={resetFilters}
                />
              )}

              <Pagination page={page} total={pageCount} onChange={setPage} />
            </section>
          </>
        )}
      </div>
      <Toast message={toast} onClose={() => setToast(null)} />
    </SiteLayout>
  );
}